import type { SharedAdventureMap, PlacePreviewFeature, PlaceGeoJSON, RelatedPlace } from "~/lib/api";
import type { DetailLevel } from "~/lib/detailLevel";
import { AdventureMapView } from "~/components/adventure-map";
import { PlaceDetailView } from "~/components/place/PlaceDetailView";
import PlaceList from "~/components/place/PlaceList";
import { PanelSection } from "~/components/ui/panel-section";

interface ViewerPanelContentProps {
  map: SharedAdventureMap;
  places: PlaceGeoJSON;
  selectedPlace: PlacePreviewFeature | null;
  details?: DetailLevel;
  onPlaceDeselect: () => void;
  onSelectRelatedPlace?: (place: RelatedPlace) => void;
  onShare: () => void;
  emptyState?: React.ReactNode;
}

const defaultEmptyState = (
  <div className="px-4 py-8 text-center text-muted-foreground">
    <p className="text-sm">No places on this map yet</p>
  </div>
);

/**
 * Content for the viewer panel (public map viewer).
 * Read-only drill-down: map overview → place details.
 */
export function ViewerPanelContent({
  map,
  places,
  selectedPlace,
  details = "full",
  onPlaceDeselect,
  onSelectRelatedPlace,
  onShare,
  emptyState = defaultEmptyState,
}: ViewerPanelContentProps) {
  // Show place detail view when a place is selected (read-only, no actions)
  if (selectedPlace) {
    return (
      <PlaceDetailView
        place={selectedPlace.properties}
        onBack={onPlaceDeselect}
        details={details}
        readOnly
        onSelectRelatedPlace={onSelectRelatedPlace}
      />
    );
  }

  // Default: show map overview with place list (read-only, no onUpdate)
  return (
    <AdventureMapView map={map} onShare={onShare}>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Places</PanelSection.Title>
        <PanelSection.Content className="px-0">
          <PlaceList places={places.features} selectedId={null} emptyState={emptyState} />
        </PanelSection.Content>
      </PanelSection.Root>
    </AdventureMapView>
  );
}
